import { Helmet } from "react-helmet-async";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Shield, Scale, Building2, Briefcase, HardHat, Calendar, Clock, BookOpen, MapPin, Loader2 } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useBlogPosts, useBlogPost } from "@/hooks/useBlogPosts";
import { blogPosts as staticBlogPosts, getBlogPost as getStaticBlogPost } from "@/data/blogPosts";

const iconMap: Record<string, React.ReactNode> = {
  Shield: <Shield className="w-8 h-8" />,
  Scale: <Scale className="w-8 h-8" />,
  Building2: <Building2 className="w-8 h-8" />,
  Briefcase: <Briefcase className="w-8 h-8" />,
  HardHat: <HardHat className="w-8 h-8" />,
};

const BlogIndex = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const ahpDark = isAhp && isDark;
  const [searchParams] = useSearchParams();
  const stateFilter = searchParams.get("state") || undefined;

  // Query database, fall back to static data on error
  const { data: dbPosts, isLoading, isError } = useBlogPosts({
    brandKey: brand.key,
    stateCode: stateFilter,
  });

  // Normalize static posts to match DB shape for fallback
  const posts = isError || (!isLoading && (!dbPosts || dbPosts.length === 0))
    ? staticBlogPosts.map(p => ({
        slug: p.slug,
        title: p.title,
        meta_title: p.metaTitle,
        meta_description: p.metaDescription,
        keywords: p.keywords,
        audience: p.audience,
        hero_icon: p.heroIcon,
        publish_date: p.publishDate,
        read_time: p.readTime,
        sections: p.sections,
        cta: p.cta,
        state_code: null as string | null,
        content_type: 'article',
      }))
    : (dbPosts ?? []).map(p => ({
        slug: p.slug,
        title: p.title,
        meta_title: p.meta_title,
        meta_description: p.meta_description,
        keywords: p.keywords,
        audience: p.audience,
        hero_icon: p.hero_icon,
        publish_date: p.publish_date,
        read_time: p.read_time,
        sections: p.sections,
        cta: p.cta,
        state_code: p.state_code,
        content_type: p.content_type,
      }));

  const cream = ahpDark ? '#0a1628' : isAhp ? '#FAF9F6' : undefined;
  const ink = ahpDark ? '#f0ead6' : isAhp ? '#1B1B1B' : undefined;
  const navy = ahpDark ? '#7da6d4' : '#002868';
  const red = ahpDark ? '#e85d6c' : '#B22234';
  const cardBg = ahpDark ? '#111f36' : isAhp ? '#FFFFFF' : undefined;
  const border = ahpDark ? 'rgba(197,165,90,0.2)' : isAhp ? '#e5e0d5' : undefined;
  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  return (
    <>
      <Helmet>
        <title>{stateFilter ? `${stateFilter} Healthcare Pricing Insights` : 'Healthcare Pricing Insights & Guides'} | {brand.name}</title>
        <meta name="description" content="Expert guides on using hospital pricing data for medical bill advocacy, legal cases, employer benefits, insurance brokerage, and workers' compensation." />
        <meta name="keywords" content="hospital pricing blog, healthcare cost data, medical bill negotiation, hospital chargemaster analysis" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/blog${stateFilter ? `?state=${stateFilter}` : ''}`} />
        <meta property="og:title" content={`Healthcare Pricing Insights | ${brand.name}`} />
        <meta property="og:description" content="Expert guides on using hospital pricing data for medical bill advocacy, legal cases, and healthcare cost management." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/blog`} />
        <meta property="og:site_name" content={brand.name} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": `Healthcare Pricing Insights | ${brand.name}`,
          "description": "Expert guides on using hospital pricing data across industries.",
          "url": `${brand.primaryDomain}/blog`,
          "publisher": { "@type": "Organization", "name": brand.name }
        })}</script>
      </Helmet>

      <div className={isAhp ? "min-h-screen pb-24" : "min-h-screen bg-background pb-24"} style={isAhp ? { backgroundColor: cream, color: ink } : undefined}>
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <BookOpen className="w-12 h-12" style={isAhp ? { color: navy } : undefined} />
            </div>
            <h1
              className={isAhp ? "text-4xl md:text-5xl font-bold mb-4" : "text-4xl font-bold mb-4 text-foreground"}
              style={isAhp ? { fontFamily: serif, color: navy } : undefined}
            >
              {stateFilter ? `${stateFilter} Healthcare Pricing Insights` : 'Healthcare Pricing Insights'}
            </h1>
            <p
              className={isAhp ? "text-lg max-w-2xl mx-auto" : "text-lg text-muted-foreground max-w-2xl mx-auto"}
              style={isAhp ? { fontFamily: sans, color: ahpDark ? '#b0a890' : '#555' } : undefined}
            >
              Expert guides on using hospital pricing data from 9,500+ facilities. Written for the professionals who fight overcharges every day.
            </p>
            {stateFilter && (
              <Link to="/blog" className="inline-flex items-center gap-1 mt-3 text-sm" style={isAhp ? { color: navy } : undefined}>
                <ArrowLeft className="w-3 h-3" /> View all articles
              </Link>
            )}
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={isAhp ? { color: navy } : undefined} />
            </div>
          )}

          {/* Blog Post Cards */}
          {!isLoading && (
            <div className="space-y-6">
              {posts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="block group">
                  <Card
                    className={isAhp ? "transition-all duration-200 hover:shadow-lg" : "transition-all duration-200 hover:shadow-lg hover:border-primary/30"}
                    style={isAhp ? { backgroundColor: cardBg, border: `1px solid ${border}`, transition: 'box-shadow 0.2s' } : undefined}
                  >
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-start gap-4">
                        <div
                          className={isAhp ? "flex-shrink-0 p-3 rounded-lg" : "flex-shrink-0 p-3 rounded-lg bg-primary/10"}
                          style={isAhp ? { backgroundColor: ahpDark ? 'rgba(197,165,90,0.1)' : 'rgba(0,40,104,0.08)', color: navy } : undefined}
                        >
                          {iconMap[post.hero_icon] || <Shield className="w-8 h-8" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span
                              className={isAhp ? "text-xs font-medium px-2 py-1 rounded-full" : "text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary"}
                              style={isAhp ? { backgroundColor: ahpDark ? 'rgba(232,93,108,0.15)' : 'rgba(178,34,52,0.1)', color: red, fontFamily: sans } : undefined}
                            >
                              {post.audience || post.content_type}
                            </span>
                            {post.state_code && (
                              <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(197,165,90,0.15)', color: '#8B7730' }}>
                                <MapPin className="w-3 h-3" /> {post.state_code}
                              </span>
                            )}
                            {post.publish_date && (
                              <span className="flex items-center gap-1 text-xs" style={isAhp ? { color: ahpDark ? '#888' : '#999', fontFamily: sans } : { color: '#999' }}>
                                <Calendar className="w-3 h-3" />
                                {new Date(post.publish_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                            {post.read_time && (
                              <span className="flex items-center gap-1 text-xs" style={isAhp ? { color: ahpDark ? '#888' : '#999', fontFamily: sans } : { color: '#999' }}>
                                <Clock className="w-3 h-3" />
                                {post.read_time}
                              </span>
                            )}
                          </div>
                          <h2
                            className={isAhp ? "text-xl md:text-2xl font-bold mb-2 group-hover:underline" : "text-xl md:text-2xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors"}
                            style={isAhp ? { fontFamily: serif, color: ink } : undefined}
                          >
                            {post.title}
                          </h2>
                          <p
                            className={isAhp ? "text-sm line-clamp-2" : "text-sm text-muted-foreground line-clamp-2"}
                            style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
                          >
                            {post.meta_description}
                          </p>
                          <span
                            className="inline-flex items-center gap-1 mt-3 text-sm font-medium"
                            style={isAhp ? { color: navy, fontFamily: sans } : undefined}
                          >
                            Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* CTA */}
          <div
            className={isAhp ? "mt-12 text-center p-8 rounded-xl" : "mt-12 text-center p-8 rounded-xl bg-primary/5 border"}
            style={isAhp ? { backgroundColor: ahpDark ? 'rgba(0,40,104,0.15)' : 'rgba(0,40,104,0.05)', border: `1px solid ${border}` } : undefined}
          >
            <h2
              className={isAhp ? "text-2xl font-bold mb-3" : "text-2xl font-bold mb-3 text-foreground"}
              style={isAhp ? { fontFamily: serif, color: ink } : undefined}
            >
              Ready to Access Hospital Pricing Data?
            </h2>
            <p
              className={isAhp ? "mb-6" : "mb-6 text-muted-foreground"}
              style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
            >
              Self-service plans for advocates, attorneys, employers, and brokers. No sales call required.
            </p>
            <Link to="/for-business">
              <Button
                size="lg"
                className={isAhp ? "text-white font-semibold px-8" : ""}
                style={isAhp ? { backgroundColor: navy, fontFamily: sans } : undefined}
              >
                View Business Plans <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

const BlogPostPage = ({ slug }: { slug: string }) => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const ahpDark = isAhp && isDark;

  // Try DB first, fall back to static
  const { data: dbPost, isLoading } = useBlogPost(slug);
  const staticPost = getStaticBlogPost(slug);

  // Normalize to a common shape
  const post = dbPost
    ? {
        slug: dbPost.slug,
        title: dbPost.title,
        metaTitle: dbPost.meta_title || dbPost.title,
        metaDescription: dbPost.meta_description || '',
        keywords: dbPost.keywords || '',
        audience: dbPost.audience || dbPost.content_type,
        publishDate: dbPost.publish_date || dbPost.created_at,
        readTime: dbPost.read_time || '',
        heroIcon: dbPost.hero_icon,
        sections: dbPost.sections || [],
        cta: dbPost.cta || { heading: '', text: '', buttonText: 'Learn More', buttonLink: '/for-business' },
        stateCode: dbPost.state_code,
      }
    : staticPost
    ? {
        slug: staticPost.slug,
        title: staticPost.title,
        metaTitle: staticPost.metaTitle,
        metaDescription: staticPost.metaDescription,
        keywords: staticPost.keywords,
        audience: staticPost.audience,
        publishDate: staticPost.publishDate,
        readTime: staticPost.readTime,
        heroIcon: staticPost.heroIcon,
        sections: staticPost.sections,
        cta: staticPost.cta,
        stateCode: null as string | null,
      }
    : null;

  // Also fetch related posts from DB
  const { data: relatedPosts } = useBlogPosts({ brandKey: brand.key, limit: 5 });

  const cream = ahpDark ? '#0a1628' : isAhp ? '#FAF9F6' : undefined;
  const ink = ahpDark ? '#f0ead6' : isAhp ? '#1B1B1B' : undefined;
  const navy = ahpDark ? '#7da6d4' : '#002868';
  const red = ahpDark ? '#e85d6c' : '#B22234';
  const cardBg = ahpDark ? '#111f36' : isAhp ? '#FFFFFF' : undefined;
  const border = ahpDark ? 'rgba(197,165,90,0.2)' : isAhp ? '#e5e0d5' : undefined;
  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={isAhp ? { backgroundColor: cream } : undefined}>
        <Loader2 className="w-8 h-8 animate-spin" style={isAhp ? { color: navy } : undefined} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">Back to blog</Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "datePublished": post.publishDate,
    "author": { "@type": "Organization", "name": brand.name },
    "publisher": { "@type": "Organization", "name": brand.name, "logo": { "@type": "ImageObject", "url": brand.social.ogImage } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${brand.primaryDomain}/blog/${post.slug}` }
  };

  // Related: from DB or static fallback
  const related = relatedPosts
    ? relatedPosts.filter(p => p.slug !== post.slug).slice(0, 4).map(p => ({
        slug: p.slug,
        title: p.title,
        audience: p.audience || p.content_type,
      }))
    : staticBlogPosts.filter(p => p.slug !== post.slug).slice(0, 4).map(p => ({
        slug: p.slug,
        title: p.title,
        audience: p.audience,
      }));

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/blog/${post.slug}`} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${brand.primaryDomain}/blog/${post.slug}`} />
        <meta property="og:site_name" content={brand.name} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="article:published_time" content={post.publishDate} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className={isAhp ? "min-h-screen pb-24" : "min-h-screen bg-background pb-24"} style={isAhp ? { backgroundColor: cream, color: ink } : undefined}>
        <div className="max-w-3xl mx-auto px-4 py-12">
          {/* Back link */}
          <Link
            to={post.stateCode ? `/blog?state=${post.stateCode}` : "/blog"}
            className="inline-flex items-center gap-2 mb-8 text-sm font-medium hover:underline"
            style={isAhp ? { color: navy, fontFamily: sans } : undefined}
          >
            <ArrowLeft className="w-4 h-4" /> {post.stateCode ? `${post.stateCode} articles` : 'All articles'}
          </Link>

          {/* Article header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span
                className={isAhp ? "text-xs font-medium px-2 py-1 rounded-full" : "text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary"}
                style={isAhp ? { backgroundColor: ahpDark ? 'rgba(232,93,108,0.15)' : 'rgba(178,34,52,0.1)', color: red, fontFamily: sans } : undefined}
              >
                {post.audience}
              </span>
              {post.stateCode && (
                <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(197,165,90,0.15)', color: '#8B7730' }}>
                  <MapPin className="w-3 h-3" /> {post.stateCode}
                </span>
              )}
              {post.publishDate && (
                <span className="text-xs" style={isAhp ? { color: ahpDark ? '#888' : '#999', fontFamily: sans } : { color: '#999' }}>
                  {new Date(post.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              )}
              {post.readTime && (
                <span className="text-xs" style={isAhp ? { color: ahpDark ? '#888' : '#999', fontFamily: sans } : { color: '#999' }}>
                  {post.readTime}
                </span>
              )}
            </div>
            <h1
              className={isAhp ? "text-3xl md:text-4xl font-bold leading-tight" : "text-3xl md:text-4xl font-bold leading-tight text-foreground"}
              style={isAhp ? { fontFamily: serif, color: ink } : undefined}
            >
              {post.title}
            </h1>
          </header>

          {/* Article body */}
          <article>
            {post.sections.map((section: { heading: string; content: string }, i: number) => (
              <section key={i} className="mb-10">
                <h2
                  className={isAhp ? "text-2xl font-bold mb-4" : "text-2xl font-bold mb-4 text-foreground"}
                  style={isAhp ? { fontFamily: serif, color: ink } : undefined}
                >
                  {section.heading}
                </h2>
                <div
                  className={isAhp ? "prose prose-lg max-w-none" : "prose prose-lg max-w-none text-foreground"}
                  style={isAhp ? { fontFamily: sans, color: ahpDark ? '#ccc' : '#333' } : undefined}
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </section>
            ))}
          </article>

          {/* CTA */}
          {post.cta && post.cta.heading && (
            <div
              className={isAhp ? "mt-12 p-8 rounded-xl text-center" : "mt-12 p-8 rounded-xl text-center bg-primary/5 border"}
              style={isAhp ? { backgroundColor: ahpDark ? 'rgba(0,40,104,0.15)' : 'rgba(0,40,104,0.05)', border: `1px solid ${border}` } : undefined}
            >
              <h2
                className={isAhp ? "text-2xl font-bold mb-3" : "text-2xl font-bold mb-3 text-foreground"}
                style={isAhp ? { fontFamily: serif, color: ink } : undefined}
              >
                {post.cta.heading}
              </h2>
              <p
                className={isAhp ? "mb-6" : "mb-6 text-muted-foreground"}
                style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
              >
                {post.cta.text}
              </p>
              <Link to={post.cta.buttonLink}>
                <Button
                  size="lg"
                  className={isAhp ? "text-white font-semibold px-8" : ""}
                  style={isAhp ? { backgroundColor: navy, fontFamily: sans } : undefined}
                >
                  {post.cta.buttonText} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {/* Related posts */}
          {related.length > 0 && (
            <div className="mt-12">
              <h3
                className={isAhp ? "text-xl font-bold mb-6" : "text-xl font-bold mb-6 text-foreground"}
                style={isAhp ? { fontFamily: serif, color: ink } : undefined}
              >
                More Articles
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group">
                    <Card
                      className={isAhp ? "h-full transition-all hover:shadow-md" : "h-full transition-all hover:shadow-md hover:border-primary/30"}
                      style={isAhp ? { backgroundColor: cardBg, border: `1px solid ${border}` } : undefined}
                    >
                      <CardContent className="p-4">
                        <span
                          className="text-xs font-medium"
                          style={isAhp ? { color: red, fontFamily: sans } : undefined}
                        >
                          {r.audience}
                        </span>
                        <h4
                          className={isAhp ? "text-sm font-bold mt-1 group-hover:underline line-clamp-2" : "text-sm font-bold mt-1 text-foreground group-hover:text-primary line-clamp-2"}
                          style={isAhp ? { fontFamily: serif, color: ink } : undefined}
                        >
                          {r.title}
                        </h4>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const Blog = () => {
  const { slug, stateCode } = useParams<{ slug?: string; stateCode?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // If coming from /blog/state/:stateCode route, redirect to ?state=XX
  if (stateCode && !slug) {
    // Set the state filter via search params for BlogIndex
    if (searchParams.get("state") !== stateCode) {
      setSearchParams({ state: stateCode }, { replace: true });
    }
    return <BlogIndex />;
  }

  if (slug) {
    return <BlogPostPage slug={slug} />;
  }

  return <BlogIndex />;
};

export default Blog;
